import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RouterConstants } from '../constants/router-constants';
import { SplunkLogsService } from './splunkLogs.service';

export const FEEDBACK_UNREAD_POLL_INTERVAL_MS = 2 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class FeedbackNotificationService implements OnDestroy {

  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  private pollTimerId?: ReturnType<typeof setInterval>;
  private visibilityHandler?: () => void;
  private polling = false;

  constructor(
    private readonly splunkLogsService: SplunkLogsService,
    private readonly routerConstants: RouterConstants
  ) {}

  start(): void {
    if (this.polling || this.routerConstants.getUserType() !== 'scw_admin') {
      return;
    }
    this.polling = true;
    this.fetchUnreadCount();
    this.pollTimerId = setInterval(() => this.fetchUnreadCount(), FEEDBACK_UNREAD_POLL_INTERVAL_MS);
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.fetchUnreadCount();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  refreshNow(): void {
    if (this.routerConstants.getUserType() !== 'scw_admin') {
      return;
    }
    this.fetchUnreadCount(true);
  }

  stop(): void {
    if (this.pollTimerId != null) {
      clearInterval(this.pollTimerId);
      this.pollTimerId = undefined;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }
    this.polling = false;
    this.unreadCountSubject.next(0);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private fetchUnreadCount(force = false): void {
    if (!force && typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    this.splunkLogsService.getUnreadCount().pipe(
      catchError(() => EMPTY)
    ).subscribe((resp: any) => {
      const count = Number(resp?.count ?? 0);
      this.unreadCountSubject.next(Number.isFinite(count) ? count : 0);
    });
  }
}
